import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Coins, Clock, Sparkles, Check } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const GardenPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [garden, setGarden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);

  useEffect(() => {
    fetchGarden();
    // Refresh every minute to update timers
    const interval = setInterval(fetchGarden, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchGarden = async () => {
    try {
      const response = await api.get('/coin-world/garden');
      setGarden(response.data);
    } catch (error) {
      toast.error('Could not load garden');
    } finally {
      setLoading(false);
    }
  };

  const handlePlant = async (plantType) => {
    try {
      const response = await api.post('/coin-world/garden/plant', {
        plant_id: plantType.id,
        amount: plantType.cost
      });
      
      toast.success(response.data.message);
      await fetchGarden();
      await refreshUser();
      setSelectedPlant(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not plant!');
    }
  };

  const handleHarvest = async (plantId) => {
    try {
      const response = await api.post(`/coin-world/garden/harvest/${plantId}`);
      
      toast.success(response.data.message);
      
      if (response.data.badges_earned?.length > 0) {
        response.data.badges_earned.forEach(badge => {
          toast.success(`🏆 New Badge: ${badge.name}!`, { duration: 4000 });
        });
      }
      
      await fetchGarden();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not harvest!');
    }
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Ready!';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getGrowthStage = (plant) => {
    if (plant.ready) return 4;
    const plantInfo = garden?.available_plants?.find(p => p.id === plant.plant_id);
    if (!plantInfo) return 1;
    
    const totalDuration = plantInfo.duration_days * 24 * 3600;
    const elapsed = totalDuration - plant.time_remaining;
    const progress = elapsed / totalDuration;
    
    if (progress < 0.33) return 1;
    if (progress < 0.66) return 2;
    if (progress < 1) return 3;
    return 4;
  };

  const GROWTH_EMOJIS = {
    1: '🌱',
    2: '🌿',
    3: '🪴',
    4: '✨'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">🌱</div>
          <p className="text-xl text-slate-600">Growing your garden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="garden-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/coin-world')}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <h1 className="text-xl font-bold text-slate-900 font-fredoka">Coin Garden</h1>
          </div>
          
          <div className="flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full">
            <Coins className="w-5 h-5 text-primary" />
            <span className="font-bold text-slate-900">{garden?.coins || 0}</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Info Banner */}
        <div className="card-bubbly bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center gap-4">
            <div className="text-5xl">🌻</div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-fredoka">
                Plant Coins, Grow More!
              </h2>
              <p className="text-slate-600">
                Plant coins and wait - they'll grow into even more coins!
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="card-bubbly text-center py-4">
            <p className="text-2xl font-black text-slate-900">{garden?.total_invested || 0}</p>
            <p className="text-sm text-slate-500">Total Planted</p>
          </div>
          <div className="card-bubbly text-center py-4">
            <p className="text-2xl font-black text-green-600">{garden?.total_harvested || 0}</p>
            <p className="text-sm text-slate-500">Total Harvested</p>
          </div>
        </div>

        {/* My Plants */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-3">My Plants</h3>
          
          {garden?.plants?.length === 0 ? (
            <div className="card-bubbly text-center py-8 bg-green-50">
              <div className="text-5xl mb-3">🌱</div>
              <p className="text-slate-600">No plants yet! Pick a seed below to start.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {garden?.plants?.map((plant) => {
                const stage = getGrowthStage(plant);
                const plantInfo = garden?.available_plants?.find(p => p.id === plant.plant_id);
                
                return (
                  <div
                    key={plant.id}
                    className={`card-bubbly text-center ${plant.ready ? 'bg-yellow-50 border-yellow-300 animate-pulse-soft' : 'bg-green-50'}`}
                    data-testid={`plant-${plant.id}`}
                  >
                    <div className="text-4xl mb-2">
                      {plant.ready ? plantInfo?.emoji || '🌺' : GROWTH_EMOJIS[stage]}
                    </div>
                    <p className="font-bold text-slate-900 text-sm">{plant.name}</p>
                    
                    {plant.ready ? (
                      <>
                        <p className="text-green-600 font-bold text-lg mb-2">
                          +{plant.return_amount} coins!
                        </p>
                        <button
                          onClick={() => handleHarvest(plant.id)}
                          className="w-full py-2 bg-green-500 text-white rounded-xl font-bold text-sm"
                          data-testid={`harvest-${plant.id}`}
                        >
                          Harvest! 🎉
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-center gap-1 text-slate-500 text-sm mb-2">
                          <Clock className="w-3 h-3" />
                          {formatTime(plant.time_remaining)}
                        </div>
                        <Progress 
                          value={100 - (plant.time_remaining / (plantInfo?.duration_days * 24 * 3600) * 100)} 
                          className="h-2 bg-slate-200" 
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Available Seeds */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-slate-900">Plant New Seeds</h3>
            <span className="text-sm font-semibold text-slate-500" data-testid="plant-slots">
              {garden?.active_count || 0}/{garden?.max_active_plants || 3} slots used
            </span>
          </div>
          
          {(garden?.active_count || 0) >= (garden?.max_active_plants || 3) && (
            <div className="card-bubbly bg-amber-50 border-amber-200 mb-4 text-center py-3" data-testid="garden-full-banner">
              <p className="text-sm text-amber-700 font-semibold">
                Your garden is full! Harvest a plant to free up a slot.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {garden?.available_plants?.map((plant) => {
              const canAfford = (garden?.coins || 0) >= plant.cost;
              const gardenFull = (garden?.active_count || 0) >= (garden?.max_active_plants || 3);
              const canPlant = canAfford && !gardenFull;
              
              return (
                <button
                  key={plant.id}
                  onClick={() => canPlant && setSelectedPlant(plant)}
                  disabled={!canPlant}
                  className={`card-bubbly text-center hover-bounce tap-shrink ${!canPlant ? 'opacity-50' : ''}`}
                  data-testid={`seed-${plant.id}`}
                >
                  <div className="text-4xl mb-2">{plant.emoji}</div>
                  <p className="font-bold text-slate-900 text-sm">{plant.name}</p>
                  <p className="text-xs text-slate-500 mb-2">{plant.description}</p>
                  
                  <div className="bg-slate-100 rounded-xl p-2 mb-2">
                    <p className="text-xs text-slate-600">Plant: {plant.cost} coins</p>
                    <p className="text-xs text-slate-600">Wait: {plant.duration_days} day(s)</p>
                    <p className="text-sm font-bold text-green-600">Get: {plant.return_amount} coins!</p>
                  </div>
                  
                  <div className="py-1 bg-primary rounded-xl font-bold text-sm text-black">
                    {gardenFull ? 'Garden Full' : canAfford ? 'Plant!' : 'Need more coins'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Plant Confirmation Modal */}
        {selectedPlant && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop">
              <div className="text-center mb-6">
                <div className="text-6xl mb-3">{selectedPlant.emoji}</div>
                <h3 className="font-bold text-xl text-slate-900">{selectedPlant.name}</h3>
                <p className="text-slate-500">{selectedPlant.description}</p>
              </div>

              <div className="bg-green-50 rounded-2xl p-4 mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">You plant:</span>
                  <span className="font-bold">{selectedPlant.cost} coins</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Wait time:</span>
                  <span className="font-bold">{selectedPlant.duration_days} day(s)</span>
                </div>
                <div className="flex justify-between border-t border-green-200 pt-2 mt-2">
                  <span className="text-slate-600">You get:</span>
                  <span className="font-bold text-green-600">{selectedPlant.return_amount} coins!</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Profit:</span>
                  <span className="font-bold text-green-600">+{selectedPlant.return_amount - selectedPlant.cost} coins</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedPlant(null)}
                  className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePlant(selectedPlant)}
                  className="flex-1 py-3 bg-green-500 rounded-2xl font-bold text-white"
                  data-testid="confirm-plant-btn"
                >
                  Plant It! 🌱
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="card-bubbly bg-yellow-50">
          <h3 className="font-bold text-slate-900 mb-2">Garden Tips</h3>
          <ul className="space-y-1 text-sm text-slate-700">
            <li>&#x2022; You can grow up to {garden?.max_active_plants || 3} plants at a time</li>
            <li>&#x2022; Plants grow even when you're not playing!</li>
            <li>&#x2022; Longer wait = more coins returned</li>
            <li>&#x2022; Your coins are always safe - plants never fail!</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default GardenPage;
